import { isBoolean, isPlainObject } from 'remeda'
import { readRenderedLiveProp } from './live-props'

// Private types ---------------------------------------------------------------

type LiveEventReference = unknown

type VisibilityCommand = 'dialog' | 'drawer' | 'dropdown' | 'popover'

type VisibilityActions = Readonly<{
  hide: () => void
  show: () => void
}>

// Public types ----------------------------------------------------------------

export type VisibilityHookContext = {
  readonly el: HTMLElement
  readonly handleEvent: (
    event: string,
    callback: (payload: unknown) => void
  ) => LiveEventReference
  readonly removeHandleEvent?: (reference: LiveEventReference) => void
}

// Private functions -----------------------------------------------------------

// Checks whether a window command targets this exact element.
const targetsElement = (element: HTMLElement, event: Event): boolean =>
  event.target === element

// Checks a LiveView command payload for the expected element id.
const hasMatchingId = (payload: unknown, id: string): boolean =>
  isPlainObject(payload) && payload.id === id

// Public functions ------------------------------------------------------------

// Reads the rendered server intent for an interactive open property.
export const readServerOpen = (element: HTMLElement): boolean | undefined => {
  const value = readRenderedLiveProp(element, 'interactive', 'open')

  return isBoolean(value) ? value : undefined
}

// Connects DOM and LiveView visibility commands with one cleanup.
export const setupVisibilityCommands = (
  context: VisibilityHookContext,
  name: VisibilityCommand,
  actions: VisibilityActions
): (() => void) => {
  const showEvent = `${name}:show`
  const hideEvent = `${name}:hide`
  const handleWindowShow = (event: Event): void => {
    if (targetsElement(context.el, event)) {
      actions.show()
    }
  }
  const handleWindowHide = (event: Event): void => {
    if (targetsElement(context.el, event)) {
      actions.hide()
    }
  }

  window.addEventListener(showEvent, handleWindowShow)
  window.addEventListener(hideEvent, handleWindowHide)

  const showReference = context.handleEvent(showEvent, (payload) => {
    if (hasMatchingId(payload, context.el.id)) {
      actions.show()
    }
  })
  const hideReference = context.handleEvent(hideEvent, (payload) => {
    if (hasMatchingId(payload, context.el.id)) {
      actions.hide()
    }
  })

  return () => {
    window.removeEventListener(showEvent, handleWindowShow)
    window.removeEventListener(hideEvent, handleWindowHide)
    context.removeHandleEvent?.(showReference)
    context.removeHandleEvent?.(hideReference)
  }
}
